import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ShieldCheck, Download, FileText, Scale, ExternalLink, Shield, Network, Lock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { complianceDocs, companyInfo } from '../data/mockData';

const Compliance = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Compliance & Certifications | SALAR Technologies LLC</title>
        <meta name="description" content="SALAR Technologies federal-contracting compliance posture: CMMC Level 1 FCI Self-Assessment, FAR 52.204-21 cybersecurity readiness, AI use & engineering governance, and prime/subcontractor target pack — all public-safe and prime-ready." />
        <meta property="og:title" content="SALAR Compliance & Certifications" />
        <meta property="og:description" content="CMMC Level 1, FAR 52.204-21, AI Governance — federal-contracting compliance documentation for prime contractors and contracting officers." />
        <meta property="og:url" content="https://www.salartechnologies.com/compliance" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.salartechnologies.com/compliance" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      {/* Hero */}
      <section
        className="relative text-white py-20"
        style={{
          backgroundImage: "linear-gradient(rgba(10,22,40,0.65), rgba(10,22,40,0.9)), url('/img/salar_capability_trust_assurance.webp')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        role="img"
        aria-label="Abstract circuit-board shield visual representing provenance, cybersecurity, and trusted electronics assurance."
        data-testid="compliance-hero"
      >
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <ShieldCheck className="w-20 h-20 mx-auto mb-6 text-blue-200" />
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">Compliance & Certifications</h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              Federal-contracting compliance posture, cybersecurity readiness, and governance documentation
              for prime contractors, contracting officers, and program offices conducting supplier diligence.
            </p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white/5 backdrop-blur-sm">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div
              className="mb-8 p-4 rounded-md border border-cyan-500/30 bg-blue-950/40 text-center"
              data-testid="compliance-self-attestation-disclaimer"
            >
              <p className="text-sm text-blue-100 leading-relaxed italic">
                Public readiness statements are company self-attestations and do not represent government
                certification, contract award, or agency endorsement.
              </p>
            </div>
            <h2 className="text-3xl font-bold text-white mb-6 text-center">Federal Contracting Readiness</h2>
            <p className="text-lg text-blue-100 leading-relaxed text-center mb-8">
              SALAR Technologies maintains a layered compliance stack aligned to U.S. federal contracting
              requirements — covering CMMC Level 1, FAR 52.204-21 basic safeguarding, AI-use governance, and
              prime/subcontractor engagement readiness. The documents below are public-safe artifacts intended
              for supplier-vetting, on-boarding diligence, and teaming-partner evaluation.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div className="p-6 bg-blue-900/30 rounded-lg border border-blue-500/30">
                <div className="text-3xl font-bold text-white mb-2">CMMC L1</div>
                <div className="text-sm text-blue-200">FCI Self-Assessment</div>
              </div>
              <div className="p-6 bg-blue-900/30 rounded-lg border border-blue-500/30">
                <div className="text-3xl font-bold text-white mb-2">FAR 52.204-21</div>
                <div className="text-sm text-blue-200">Baseline Cybersecurity</div>
              </div>
              <div className="p-6 bg-blue-900/30 rounded-lg border border-blue-500/30">
                <div className="text-3xl font-bold text-white mb-2">AI Governance</div>
                <div className="text-sm text-blue-200">Responsible-Use Policy</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Document List */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto space-y-6">
            {complianceDocs.map((doc, idx) => (
              <Card
                key={doc.id}
                data-testid={`compliance-doc-${doc.id}`}
                className="border-blue-500/30 bg-blue-900/20 backdrop-blur-sm hover:bg-blue-900/30 transition-all"
              >
                <CardHeader>
                  <div className="flex items-start justify-between flex-wrap gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-cyan-700 rounded flex items-center justify-center border-2 border-blue-400/30 flex-shrink-0">
                        <FileText className="text-white" size={26} />
                      </div>
                      <div className="flex-1">
                        <Badge variant="outline" className="border-blue-400 text-blue-200 mb-2">
                          {String(idx + 1).padStart(2, '0')} · {doc.fileType} · {doc.size}
                        </Badge>
                        <CardTitle className="text-xl text-white leading-snug">{doc.title}</CardTitle>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription
                    className="text-base text-blue-100 leading-relaxed mb-6"
                    data-testid={`compliance-intro-${doc.id}`}
                  >
                    {doc.intro}
                  </CardDescription>
                  <a href={doc.url} target="_blank" rel="noopener noreferrer">
                    <Button
                      data-testid={`compliance-download-${doc.id}`}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Download className="mr-2" size={18} />
                      Download {doc.fileType}
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Public Compliance Statements — May 2, 2026 (add-only subsection) */}
      <section
        className="py-16 border-t border-blue-800/30"
        style={{ background: '#0F1A2B' }}
        data-testid="public-compliance-statements-section"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <Badge variant="outline" className="border-cyan-400 text-cyan-300 mb-4 tracking-widest text-xs">
                RELEASED · MAY 2, 2026
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Public Compliance Statements &mdash; May 2, 2026
              </h2>
              <p className="text-base text-blue-100 leading-relaxed max-w-3xl mx-auto">
                Three public-safe self-attestations covering Section 889 screening, supply-chain compliance
                approach, and cybersecurity / federal safeguarding readiness. Provided for federal, defense,
                prime-contractor, and partner review.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  id: 'a1-section-889',
                  Icon: Shield,
                  title: 'Section 889 Compliance Statement',
                  desc: 'Explains what Section 889 is, why it matters for federal contracting, and how SALAR screens against covered telecommunications and video-surveillance equipment or services.',
                  file: '/pdf/SALAR_A1_Section_889_Compliance_Statement.pdf',
                  filename: 'SALAR_A1_Section_889_Compliance_Statement',
                },
                {
                  id: 'a2-supply-chain',
                  Icon: Network,
                  title: 'Supply Chain Compliance Appendix',
                  desc: "Describes SALAR's public approach to vendor-level visibility, prohibited-source exclusion, alternate-source planning, and source-confidence scoring.",
                  file: '/pdf/SALAR_A2_Supply_Chain_Compliance_Appendix.pdf',
                  filename: 'SALAR_A2_Supply_Chain_Compliance_Appendix',
                },
                {
                  id: 'a3-cybersecurity',
                  Icon: Lock,
                  title: 'Cybersecurity & Federal Safeguarding Readiness',
                  desc: "Describes SALAR's public readiness posture for FAR 52.204-21 basic safeguarding, Federal Contract Information boundaries, and CMMC Level 1 self-assessment concepts.",
                  file: '/pdf/SALAR_A3_Cybersecurity_Federal_Safeguarding_Readiness.pdf',
                  filename: 'SALAR_A3_Cybersecurity_Federal_Safeguarding_Readiness',
                },
              ].map(({ id, Icon, title, desc, file, filename }) => (
                <Card
                  key={id}
                  data-testid={`public-compliance-${id}`}
                  className="border-cyan-500/30 bg-[#1A2744] hover:bg-[#1F2E50] transition-all flex flex-col"
                  style={{ borderLeft: '4px solid #1B6EC2' }}
                >
                  <CardHeader>
                    <div className="w-14 h-14 rounded-xl bg-blue-950/60 border border-cyan-400/40 flex items-center justify-center mb-3">
                      <Icon className="text-cyan-300" size={26} />
                    </div>
                    <Badge variant="outline" className="border-cyan-400 text-cyan-200 mb-2 w-fit text-xs">
                      Public Statement &middot; PDF
                    </Badge>
                    <CardTitle className="text-lg text-white leading-snug">{title}</CardTitle>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col">
                    <CardDescription className="text-sm text-blue-100 leading-relaxed mb-5 flex-1">
                      {desc}
                    </CardDescription>
                    <a href={file} target="_blank" rel="noopener noreferrer" className="block">
                      <Button
                        data-testid={`public-compliance-download-${id}`}
                        onClick={() => {
                          if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
                            window.gtag('event', 'file_download', {
                              file_name: filename,
                              file_extension: 'pdf',
                              link_url: file,
                            });
                          }
                        }}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <Download className="mr-2" size={16} />
                        Download PDF
                      </Button>
                    </a>
                  </CardContent>
                </Card>
              ))}
            </div>

            <p className="text-xs text-blue-300/80 italic text-center mt-8 max-w-3xl mx-auto leading-relaxed">
              These documents are public company self-attestations and do not represent government certification,
              contract award, or CMMC Level 2 certification.
            </p>
          </div>
        </div>
      </section>

      {/* Defensive IP & Patent Risk Posture */}
      <section className="py-16 bg-blue-950/20" data-testid="defensive-ip-section">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <Card className="border-cyan-400/40 bg-gradient-to-br from-blue-900/40 to-cyan-900/20 backdrop-blur-sm">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-blue-900/40 border-2 border-cyan-400/40 flex items-center justify-center flex-shrink-0">
                    <Scale className="text-cyan-300" size={26} />
                  </div>
                  <div className="flex-1">
                    <Badge variant="outline" className="border-cyan-400 text-cyan-200 mb-3">
                      Corporate Governance · IP Risk Posture
                    </Badge>
                    <CardTitle className="text-2xl md:text-3xl text-white leading-tight">
                      Defensive IP &amp; Patent Risk Posture
                    </CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-blue-100 leading-relaxed">
                  SALAR Technologies LLC is a <strong className="text-white">LOT Network member</strong> as part of
                  its defensive intellectual-property risk posture. LOT Network is a recognized defensive patent
                  community designed to reduce exposure to patent assertion entities, sometimes called
                  &ldquo;patent trolls.&rdquo;
                </p>
                <p className="text-blue-100 leading-relaxed">
                  For SALAR, this membership supports responsible patent ownership, partner confidence, and
                  long-term innovation protection. SALAR&apos;s patent-aligned work is focused on electronics
                  reliability, semiconductor supply-chain intelligence, provenance and counterfeit-risk control,
                  manufacturing stability, and trusted microelectronics process assurance.
                </p>
                <p className="text-blue-100 leading-relaxed">
                  This membership does not replace legal review, patent prosecution, cybersecurity compliance,
                  CMMC readiness, SAM.gov registration, or federal acquisition requirements. It is maintained as
                  part of SALAR&apos;s broader corporate governance, IP hygiene, and risk-reduction posture.
                </p>

                <div className="bg-blue-950/40 border-l-4 border-cyan-400 rounded p-4 mt-2">
                  <p className="text-blue-100 text-sm leading-relaxed">
                    <span className="text-cyan-300 font-semibold">Defensive IP posture: </span>
                    SALAR Technologies LLC is a LOT Network member for patent-assertion-entity risk mitigation.
                  </p>
                </div>

                <div className="pt-3">
                  <a
                    href="https://lotnet.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="lot-network-link"
                  >
                    <Button variant="outline" className="border-cyan-400 text-cyan-200 hover:bg-cyan-400 hover:text-[#0a1628]">
                      <ExternalLink className="mr-2" size={16} />
                      Visit LOT Network
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-16 bg-white/5 backdrop-blur-sm">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Need additional compliance documentation?</h2>
            <p className="text-blue-100 leading-relaxed mb-6">
              For supplier on-boarding packages, NDA-gated documentation, prime-contractor teaming sheets,
              or compliance-question routing, contact SALAR directly.
            </p>
            <a href={`mailto:${companyInfo.emails.general}?subject=Compliance%20%26%20Certifications%20Inquiry`}>
              <Button
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="compliance-contact-btn"
              >
                Contact SALAR Compliance
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Compliance;
